import Loader from '@/components/ui/Loader';
import Modal from '@/components/ui/Modal';
import {
  useFindDistributorsToShareCircuitsWith,
  useGetDistributorsNetworks,
  useShareCircuitsWithAgencies,
} from '@/hooks/useDistributors';
import AgencySelectionTable from './AgencySelectionTable';
import { useMemo, useState } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import { LabelValue } from '@/types/global';
import { useForm, useWatch } from 'react-hook-form';
import StyledInput from '@/components/styled/StyledInput';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledBooleanCheckbox from '@/components/styled/StyledBooleanCheckbox';
import SearchIcon from '@/assets/icons/search.svg?react';
import { useToast } from '@/hooks/useToast';
import useTableControls from '@/hooks/useTableControls';
import { useTranslation } from '@/i18n';

interface SelectAgenciesModalProps {
  circuitIds: string[];
  closeModal: () => void;
}

const SelectAgenciesModal = ({
  circuitIds,
  closeModal,
}: SelectAgenciesModalProps) => {
  const { t } = useTranslation();
  const { control } = useForm<{
    agencyName: string;
    network?: LabelValue<string>;
    shouldShareWithAllCurrentDistributors: boolean;
    shouldShareWithAllFutureDistributors: boolean;
    selectedNetworks: LabelValue<string>[];
  }>({
    mode: 'onChange',
    defaultValues: {
      agencyName: '',
      network: undefined,
      shouldShareWithAllCurrentDistributors: false,
      shouldShareWithAllFutureDistributors: false,
      selectedNetworks: [],
    },
  });

  const searchValue = useWatch({ control, name: 'agencyName' });
  const networkFilterValue = useWatch({ control, name: 'network' });
  const shouldShareWithAllFutureDistributors = useWatch({
    control,
    name: 'shouldShareWithAllFutureDistributors',
  });
  const shouldShareWithAllCurrentDistributors = useWatch({
    control,
    name: 'shouldShareWithAllCurrentDistributors',
  });
  const selectedNetworks = useWatch({ control, name: 'selectedNetworks' });

  const selectedNetworksIds = useMemo(
    () => selectedNetworks.map((network) => network.value),
    [selectedNetworks],
  );

  const {
    data: agencies,
    isLoading,
    isSuccess,
    isFetching,
  } = useFindDistributorsToShareCircuitsWith({
    circuitIds,
  });

  const { data: networksOptions } = useGetDistributorsNetworks((networks) =>
    networks.map((network) => ({
      label: network.name,
      value: network.id,
    })),
  );

  const { mutateAsync: shareCircuitsWithAgencies } =
    useShareCircuitsWithAgencies();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { sorting, setSorting, sortField, sortOrder } = useTableControls();

  const filteredAgencies = useMemo(
    () =>
      (agencies ?? [])
        .filter((agency) =>
          agency.name
            .toLocaleLowerCase()
            .includes(searchValue.toLocaleLowerCase()),
        )
        .filter((agency) =>
          networkFilterValue
            ? agency.network?.id === networkFilterValue.value
            : true,
        )
        .toSorted((agency1, agency2) => {
          switch (sortField) {
            case 'network_name': {
              const networkName1 = agency1.network?.name ?? '';
              const networkName2 = agency2.network?.name ?? '';
              return sortOrder === 'asc'
                ? networkName1.localeCompare(networkName2)
                : networkName2.localeCompare(networkName1);
            }
            case 'created_at':
              return sortOrder === 'asc'
                ? agency1.createdAt.getTime() - agency2.createdAt.getTime()
                : agency2.createdAt.getTime() - agency1.createdAt.getTime();
            default:
              return sortOrder === 'asc'
                ? agency1.name.localeCompare(agency2.name)
                : agency2.name.localeCompare(agency1.name);
          }
        }),
    [agencies, networkFilterValue, searchValue, sortField, sortOrder],
  );

  const selectedAgencies = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  const { toastAsync } = useToast();

  return (
    <Modal
      title={t('modal.selectAgencies.title')}
      containerClassName="max-w-3xl w-full"
      actionOnClick={() => {
        toastAsync(
          shareCircuitsWithAgencies({
            circuitIds,
            distributorIds: selectedAgencies,
            shouldShareAllCurrentCircuits: false,
            shouldShareAllFutureCircuits: false,
            shouldShareCircuitsWithAllFutureDistributors:
              shouldShareWithAllFutureDistributors,
            networkIds: selectedNetworksIds,
            distributorsToRemoveIds:
              agencies
                ?.filter((agency) => agency.allCircuitsAreShared)
                .map((agency) => agency.id)
                .filter((agencyId) => !selectedAgencies.includes(agencyId)) ??
              [],
          }),
          {
            success: t('modal.selectAgencies.successMessage'),
          },
        );
      }}
      closeModal={closeModal}
      actionTitle={t('modal.selectAgencies.actionTitle')}
    >
      {isLoading || isFetching ? (
        <Loader />
      ) : (
        isSuccess && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-600">
                {t('modal.selectAgencies.filterBy')}
              </span>
              <div className="flex gap-4">
                <StyledInput
                  control={control}
                  name="agencyName"
                  placeholder={t('modal.selectAgencies.quickSearch')}
                  icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
                />
                <StyledSelect
                  control={control}
                  name="network"
                  options={networksOptions ?? []}
                  placeholder={t('modal.selectAgencies.networkPlaceholder')}
                  isClearable
                />
              </div>
            </div>
            <span className="text-xs font-medium text-gray-600">
              {t('modal.selectAgencies.yourSelection')}
            </span>
            <div className="flex gap-4 w-full">
              <div className="w-full">
                <StyledBooleanCheckbox
                  control={control}
                  name="shouldShareWithAllCurrentDistributors"
                  optionLabel={t(
                    'modal.selectAgencies.shareWithAllCurrentDistributors',
                  )}
                  className="text-gray-700"
                />
              </div>
              <div className="w-full">
                <StyledBooleanCheckbox
                  control={control}
                  name="shouldShareWithAllFutureDistributors"
                  optionLabel={t(
                    'modal.selectAgencies.shareWithAllFutureDistributors',
                  )}
                  className="text-gray-700"
                />
              </div>
            </div>
            <span className="text-sm font-medium text-gray-600">
              {t('modal.selectAgencies.allNetworks')}
            </span>
            <StyledSelect
              control={control}
              name="selectedNetworks"
              options={networksOptions ?? []}
              placeholder={t('modal.selectAgencies.allNetworksPlaceholder')}
              isMulti
            />
            <AgencySelectionTable
              agencies={filteredAgencies}
              setSelectedAgencyRows={setRowSelection}
              shouldSelectAll={shouldShareWithAllCurrentDistributors}
              selectedNetworks={selectedNetworksIds}
              sorting={sorting}
              setSorting={setSorting}
            />
          </div>
        )
      )}
    </Modal>
  );
};

export default SelectAgenciesModal;
